import { Icon, Menu, MenuItem, Position } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomButtonGroup from "components/Atoms/CustomButtonGroup"
import CustomCard from "components/Atoms/CustomCard"
import CustomPopover from "components/Atoms/CustomPopover"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import ReactTableHeader from "components/Common/ReactTableHeader"
import TablePagination from "components/Molecules/TablePagination"
import ActionMenu from "components/Organisms/ActionMenu"
import ActionMenuItem from "components/Organisms/ActionMenuItem"
import DataCell, { Subtitle, Title } from "components/Organisms/table/DataCell"
import CustomReactTable from "components/Templates/CustomReactTable"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import relativeTime from "dayjs/plugin/relativeTime"
import { useAllTaskLogsQuery } from "generated/graphql"
import { useAllPeriodicTasks } from "graphql/periodic-tasks/periodic-tasks.hooks"
import { useSearchFilter } from "hooks/searchFilterHooks"
import useSearchParams from "hooks/useSearchParams"
import { omit } from "lodash-es"
import qs from "query-string"
import React, { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { mountStoreDevtool } from "simple-zustand-devtools"
import { useFilter } from "store/search-filter/search-filter.reducer"
import { createSelectorStore } from "store/selector.store"
import InterfaceFilter from "./InterfaceFilter"
import ReTriggerDialog from "./ReTriggerDialog"
import TaskLogs from "./TaskLogs"
import TaskSelector from "./TaskSelector"
import TaskSelectorAll from "./TaskSelectorAll"
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)

const PAGE_SIZE = 20
// import { useRetailGroups } from "graphql/vendor/vendor.hooks";

export const useTaskSelector = createSelectorStore()
mountStoreDevtool("PeriodicTaskSelector", useTaskSelector)
const Interfaces = () => {
  const setResultSet = useTaskSelector(store => store.setResultSet)
  const [filter, setFilter] = useSearchFilter()
  const [filterState] = useFilter("interfaces")
  const [query, setQuery] = useSearchParams()
  const isExtra = query.type === "extra"

  const page = parseInt(filter.page)
  const { data, loading, error } = useAllPeriodicTasks(
    {
      first: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      query: filter.search,
      filter: {
        ...omit(filterState, ["startTime"]),
        task: filterState.task?.id,
        isOk: filterState.isOk ?? undefined,
        startTime_Lte:
          filterState.startTime?.[1] &&
          dayjs(filterState.startTime?.[1]).toISOString(),
        startTime_Gte:
          filterState.startTime?.[0] &&
          dayjs(filterState.startTime?.[0]).toISOString(),
      },
    },
    isExtra
  )

  const {
    data: eData,
    loading: eLoading,
    error: eError,
  } = useAllTaskLogsQuery({
    variables: {
      first: PAGE_SIZE,
      skip: page * PAGE_SIZE,
      query: filter.search,
      filter: {
        ...omit(filterState, ["startTime", "containsPeriodicTask"]),
        isOk: filterState.isOk ?? undefined,
        periodicTaskIsNull:
          filterState.containsPeriodicTask === true
            ? false
            : filterState.containsPeriodicTask === false
            ? true
            : undefined,
        createdAt_Lte:
          filterState.startTime?.[1] &&
          dayjs(filterState.startTime?.[1]).format("YYYY-MM-DD"),
        createdAt_Gte:
          filterState.startTime?.[0] &&
          dayjs(filterState.startTime?.[0]).format("YYYY-MM-DD"),
      },
    },
    skip: !isExtra,
  })

  const tasks = data?.allPeriodicTasks
  const extraTasks = eData?.allTaskLogs

  useEffect(() => {
    if (isExtra) {
      if (extraTasks?.length) {
        setResultSet(extraTasks)
      }
    } else {
      if (tasks?.length) {
        setResultSet(tasks)
      }
    }
  }, [tasks, extraTasks, isExtra, setResultSet])
  // const groups = useRetailGroups();
  if (error) return <Error message={error?.message} />

  const COLUMNS = [
    {
      Header: "#",
      width: 45,
      sortable: false,
      resizable: false,
      className: "text-center",
      Cell: ({ index }) => PAGE_SIZE * ((page || 1) - 1) + index + 1,
    },
    {
      width: 50,
      accessor: "id",
      sortable: false,
      Header() {
        return <TaskSelectorAll />
      },
      Cell({ original }) {
        return <TaskSelector value={original} />
      },
    },
    {
      width: 50,
      Header: "",
      sortable: false,
      Cell({ original }) {
        return (
          <CustomPopover
            position={Position.BOTTOM_LEFT}
            minimal={true}
            content={
              <Menu>
                <MenuItem
                  text="Re-trigger"
                  onClick={() =>
                    setFilter(prev => ({
                      ...prev,
                      task: original.id,
                      view: "trigger",
                      name: original.task,
                    }))
                  }
                />
              </Menu>
            }
          >
            <CustomButton
              style={{ transform: "rotate(90deg)" }}
              icon="more"
              minimal={true}
            />
          </CustomPopover>
        )
      },
    },
    {
      width: 240,
      Header: "Task Name",
      accessor: "task",
      Cell({ value, original }) {
        const location = useLocation()
        return (
          <StyledLink
            to={{
              pathname: location.pathname,
              search: qs.stringify({
                task: original.id,
                search: filter.search,
                view: "logs",
              }),
            }}
          >
            {value}
          </StyledLink>
        )
      },
    },

    {
      Header: "Description",
      accessor: "name",
    },
    {
      width: 130,
      Header: "Request Status",
      accessor: "enabled",
      Cell({ value }) {
        return (
          <div>
            <span className="flex items-center px-3 w-fit">
              <Icon
                className="mr-2"
                icon={value ? "endorsed" : "outdated"}
                intent={value ? "success" : "none"}
              />
              {value ? "Sent" : "Not sent"}
            </span>
          </div>
        )
      },
    },
    {
      width: 130,
      Header: "Response Status",
      accessor: "latestLog.isOk",
      Cell({ value }) {
        return (
          <div>
            <span className="flex items-center px-3 w-fit">
              <Icon
                className="mr-2"
                icon={value ? "endorsed" : "delete"}
                intent={value ? "success" : "danger"}
              />
              {value ? "Pass" : "Fail"}
            </span>
          </div>
        )
      },
    },
    {
      width: 200,
      Header: "Execution Time",
      accessor: "startTime",
      Cell({ value }) {
        return value ? (
          <DataCell>
            <Title>{dayjs(value).fromNow()}</Title>
            <Subtitle>{dayjs(value).format("Do MMM YYYY hh:MM:ss a")}</Subtitle>
          </DataCell>
        ) : (
          "---"
        )
      },
    },
    {
      width: 130,
      Header: "Activity Type",
      accessor: "oneOff",
      Cell({ value }) {
        return (
          <>
            {value ? null : <Icon className="mr-1" icon={"refresh"} />}
            {value ? "One time" : "Continuous"}
          </>
        )
      },
    },
    {
      width: 240,
      Header: "Executed In (Schedule)",
      accessor: "scheduleName",
    },
  ]

  const EXTRA_COLUMNS = [
    {
      Header: "#",
      width: 45,
      sortable: false,
      resizable: false,
      className: "text-center",
      Cell: ({ index }) => PAGE_SIZE * ((page || 1) - 1) + index + 1,
    },
    {
      width: 50,
      accessor: "id",
      sortable: false,
      Header() {
        return <TaskSelectorAll />
      },
      Cell({ original }) {
        return <TaskSelector value={original} />
      },
    },
    {
      width: 240,
      Header: "Task Name",
      accessor: "taskName",
      Cell({ value, original }) {
        const location = useLocation()
        return (
          <StyledLink
            to={{
              pathname: location.pathname,
              search: qs.stringify({
                type: "extra",
                task: original.id,
                search: filter.search,
                view: "logs",
              }),
            }}
          >
            {value}
          </StyledLink>
        )
      },
    },

    {
      width: 130,
      Header: "Response Status",
      accessor: "isOk",
      Cell({ value }) {
        return (
          <div>
            <span className="flex items-center px-3 w-fit">
              <Icon
                className="mr-2"
                icon={value ? "endorsed" : "delete"}
                intent={value ? "success" : "danger"}
              />
              {value ? "Pass" : "Fail"}
            </span>
          </div>
        )
      },
    },
    {
      width: 430,
      Header: "Request Data",
      accessor: "requestData",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      width: 430,
      Header: "Response Data",
      accessor: "responseData",
      Cell({ value }) {
        return <div>{value}</div>
      },
    },
    {
      width: 430,
      Header: "Periodic Task",
      accessor: "periodicTask.name",
      Cell({ value }) {
        return <>{value}</>
      },
    },
  ]

  const selectedExtraLog =
    isExtra && filter.task && filter.view === "logs"
      ? extraTasks?.find((log: any) => log.id === filter.task)
      : null

  return (
    <>
      <CustomCard className="m-3 p-0">
        <ReactTableHeader
          icon="third-party"
          title={
            <>
              <Title>Interfaces' Tasks</Title>{" "}
              <div className="ml-3">
                <CustomButtonGroup joint>
                  <CustomButton
                    intent={isExtra ? "none" : "primary"}
                    icon="repeat"
                    text="Periodic"
                    onClick={() => setQuery({ type: null })}
                  />
                  <CustomButton
                    intent={isExtra ? "primary" : "none"}
                    icon="tick-circle"
                    text="Extra"
                    onClick={() => {
                      setQuery({ type: "extra" })
                    }}
                  />
                </CustomButtonGroup>
              </div>
            </>
          }
          filter={<InterfaceFilter />}
          value={filter?.search}
          onQueryChange={query => {
            setFilter(prev => ({
              ...prev,
              search: query,
            }))
          }}

          // filter={<BenchmarkFilter />}
        >
          <ActionMenu
            className="mr-3"
            onClick={() =>
              setFilter(prev => ({
                ...omit(prev, ["task", "name"]),
                view: "trigger",
              }))
            }
          >
            <ActionMenuItem text="Re-trigger tasks" />
          </ActionMenu>

          <TablePagination
            disabledNext={data?.allPeriodicTasks?.length < PAGE_SIZE}
            page={page}
            onPageChange={page => {
              setFilter(prev => ({
                ...prev,
                page,
              }))
            }}
          />
        </ReactTableHeader>
        {isExtra ? (
          <CustomReactTable
            showPaginationBottom={false}
            data={extraTasks ?? []}
            loading={eLoading}
            columns={EXTRA_COLUMNS}
          />
        ) : (
          <CustomReactTable
            showPaginationBottom={false}
            data={tasks ?? []}
            loading={loading}
            columns={COLUMNS}
          />
        )}
      </CustomCard>

      <TaskLogs
        taskId={filter.task}
        logs={selectedExtraLog ? [selectedExtraLog] : undefined}
        title={
          selectedExtraLog ? `${selectedExtraLog.taskName} | Logs` : undefined
        }
        isOpen={filter.task && filter.view === "logs" ? true : false}
        onClose={() => setFilter(prev => omit(prev, ["task", "view"]))}
      />
      <ReTriggerDialog />
    </>
  )
}

export default Interfaces
